/**
 * Клиент API.
 *
 * Access-токен живёт только в памяти вкладки — так его не достать из
 * localStorage при XSS. Он короткий (15 минут), и когда протухает,
 * клиент сам молча обновляет его по httpOnly-куке и повторяет запрос.
 * Пользователь этого не замечает и не разлогинивается посреди работы.
 */

let accessToken: string | null = null;

export const setToken = (token: string | null) => {
  accessToken = token;
};
export const getToken = () => accessToken;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

/** Обновление токена. Один общий промис, чтобы параллельные запросы не дёргали /refresh пачкой. */
let refreshing: Promise<boolean> | null = null;

async function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Сбрасываем на следующем тике, чтобы ожидающие успели прочитать результат
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal };

async function request<T>(path: string, options: Options = {}, retry = true): Promise<T> {
  const res = await fetch(path, {
    method: options.method ?? 'GET',
    credentials: 'include',
    signal: options.signal,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  });

  // Токен протух — обновляем и повторяем один раз
  if (res.status === 401 && retry) {
    if (await refresh()) return request<T>(path, options, false);
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(
      res.status,
      (data as { error?: string }).error ?? 'Не удалось выполнить запрос',
      (data as { code?: string }).code,
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  refresh,
};
